/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useMemo, useState } from "react";
import { DocumentationView, IDocumentationBreadcrumb } from "../DocumentationView";
import { ToolbarButton } from "../../../../ui/components/Toolbar/ToolbarButton";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { Breadcrumbs } from "../../../../ui/components/Breadcrumbs/Breadcrumbs";
import { IBreadcrumbsItem } from "../../../../ui/components/Breadcrumbs/BreadcrumbsTypes";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";

export interface IDocumentationToolbarProps {
	view: DocumentationView;
}

export const DocumentationToolbar: FC<IDocumentationToolbarProps> = ({ view }) => {
	const [ historyState, setHistoryState ] = useState<{
		canGoBack: boolean;
		canGoForward: boolean;
		breadcrumbs: IDocumentationBreadcrumb[];
	}>(() => ({
		canGoBack: view.canGoBack(),
		canGoForward: view.canGoForward(),
		breadcrumbs: view.getBreadcrumbs()
	}));

	useSimpleEvent(
		view.onHistoryChange,
		() => {
			setHistoryState({
				canGoBack: view.canGoBack(),
				canGoForward: view.canGoForward(),
				breadcrumbs: view.getBreadcrumbs()
			});
		},
		[ view ]
	);

	const handleGoBack = useCallback(() => {
		view.goBack();
	}, [ view ]);

	const handleGoForward = useCallback(() => {
		view.goForward();
	}, [ view ]);

	const breadcrumbItems = useMemo<IBreadcrumbsItem[]>(() => {
		return historyState.breadcrumbs.map((crumb) => ({
			label: crumb.title,
			onClick: () => view.setSelected(crumb.path)
		}));
	}, [ historyState.breadcrumbs, view ]);

	return (
		<>
			<ToolbarButton
				icon="mdi/arrow-left"
				tooltip="Go Back"
				disabled={!historyState.canGoBack}
				onClick={handleGoBack}
			/>
			<ToolbarButton
				icon="mdi/arrow-right"
				tooltip="Go Forward"
				disabled={!historyState.canGoForward}
				onClick={handleGoForward}
			/>
			<ToolbarSpacer />
			<Breadcrumbs items={breadcrumbItems} maxVisibleCrumbs={5} />
		</>
	);
};
